// A finished game ↔ its record. Building needs a concluded `GameState`;
// reading one back replays the UCI moves, so a record that no longer replays
// (a hand-edited download, a future engine change) is refused, not half-shown.
import { GameState } from '../game/GameState.js';
import type { Outcome } from '../game/outcome.js';
import { pgnResult, sanPlies } from '../game/pgn.js';
import { getRuleSet } from '../game/rules/index.js';
import { HISTORY_SCHEMA_VERSION } from './types.js';
import type { GameRecord, RecordedClocks, RecordedSeed } from './types.js';

export interface RecordInput {
  readonly id: string;
  readonly seed: RecordedSeed;
  /** The game as it ended. */
  readonly state: GameState;
  /** The clocks after each move, in step with `state.moves`; short or empty when unknown. */
  readonly clocks: readonly (RecordedClocks | null)[];
  readonly finishedAt: number;
}

/** The record of a finished game, or null while it is still being played. */
export function buildRecord(input: RecordInput): GameRecord | null {
  const { state, seed } = input;
  if (state.outcome === null) return null;
  const result = pgnResult(state.outcome);
  if (result === '*') return null;
  const uci = state.uciMoves;
  return {
    id: input.id,
    version: HISTORY_SCHEMA_VERSION,
    createdAt: seed.createdAt,
    finishedAt: input.finishedAt,
    mode: seed.mode,
    variant: seed.variant,
    players: { white: seed.playerWhite, black: seed.playerBlack },
    seed,
    startFen: state.startFen,
    moves: sanPlies(state).map((ply, i) => ({ san: ply.san, uci: uci[i] ?? '', clocks: input.clocks[i] ?? null })),
    result,
    termination: state.outcome.termination,
  };
}

export function recordOutcome(record: GameRecord): Outcome {
  const winner = record.result === '1-0' ? 'w' : record.result === '0-1' ? 'b' : null;
  return { winner, termination: record.termination };
}

/** The finished game a record describes, or null if it does not replay. */
export function recordState(record: GameRecord): GameState | null {
  try {
    const state = GameState.fromUciMoves(record.startFen, record.moves.map((m) => m.uci), getRuleSet(record.variant));
    if (state === null) return null;
    return state.outcome === null ? state.conclude(recordOutcome(record)) : state;
  } catch {
    return null;
  }
}
